import React, { useState, useRef, useEffect } from "react";
import styles from "./navbar.module.css";
import { FiChevronDown } from "react-icons/fi";
import { FaShoppingCart, FaCalendarCheck, FaBoxOpen } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Contexts/AuthContext";
import { useCart } from "../Contexts/CartContext";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../Services/Store";
import ChangePasswordModal from "../Customs/ChangePassword/ChangePassword";
import SettingsModal from "../Customs/Settings/SettingsModal";
import { resetAttendance } from "../Services/Slices/attendanceSlice";
import NotificationBell from "./NotificationBell";

export interface UserInfo {
  user_id: number;
  username: string;
  email: string;
  mobile_number: string;
}

interface NavbarViewProps {
  user?: UserInfo | null;
}

const navConfig = [
  { label: "Home", path: "/", code: "home" },
  { label: "Shop", path: "/shop", code: null },
  {
    label: "Inventory",
    path: "/inventory",
    code: "inventory_management",
    dropdown: [
      { label: "Raw Material", path: "/raw-material" },
      { label: "Product", path: "/inventory/product-design" },
      { label: "Wood Qualities", path: "/inventory/wood-qualities" },
      { label: "Stock Statement", path: "/inventory/stock-statement" },
    ],
  },
  { label: "Sales", path: "/Sales", code: "saleorder_management" },
  { label: "Account & Customer", path: "/accounts-management", code: "accounts_management" },

  // New group: Team
  {
    label: "Employee",
    path: "/team",
    code: null,
    dropdown: [
      { label: "Employee Management", path: "/employee-management", code: "employee_management" },
      { label: "Task Management", path: "/task-management", code: "task_management" },
      { label: "Daily Worksheet", path: "/daily-worksheet", code: "task_management", adminOnly: true },
    ],
  },

  // New group: procurement
  {
    label: "Vendor-Purchase",
    path: "/procurement",
    code: null,
    dropdown: [
      { label: "Vendors", path: "/vendor-management", code: "vendor_management" },
      { label: "Purchase Orders", path: "/purchase-management", code: "purchase_management" },
      { label: "Expenses", path: "/expenses", code: "expense_management" },
    ],
  },
  {
    label: "Role Management",
    path: "/role-management",
    code: null,
    dropdown: [
      { label: "Roles Overview", path: "/role-management", code: "rba_control" },
      { label: "Access Control", path: "/access-management", code: "rba_control" },
      { label: "State & City", path: "/state-city", staffOnly: true },
    ],
  },

  // Customer-only
  { label: "My Orders", path: "/mysaleorder-management", code: "mysaleorder_management" },
];

const NavbarView: React.FC<NavbarViewProps> = ({ user }) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // small delay so users can cross tiny gaps without closing
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openMenu = (label: string) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpenDropdown(label);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenDropdown(null), 180);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setAuthenticated } = useAuth();
  const { cartItemCount } = useCart();
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const userType = useSelector((state: RootState) => state.auth.user?.user_type) || "";

  const canRead = (code: string | null | undefined) => {
    if (!code) return true;
    const found = accessList.find((item: any) => item.code === code);
    return found?.read === true;
  };

  const VERSION = "V1.1.06";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showProfileDropdown &&
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    };
    if (showProfileDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileDropdown]);

  const handleLogout = () => {
    dispatch(resetAttendance());
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    localStorage.removeItem("last_check_in_iso");
    localStorage.removeItem("current_view_user_id");
    localStorage.removeItem("current_view_username");
    setAuthenticated(false);
    navigate("/login");
  };

  const isActive = (item: any) => {
    if (item.label === "Inventory") {
      return (
        location.pathname.startsWith("/inventory") ||
        location.pathname.startsWith("/raw-material")
      );
    }
    if (item.label === "Team") {
      return (
        location.pathname.startsWith("/employee-management") ||
        location.pathname.startsWith("/task-management") ||
        location.pathname.startsWith("/daily-worksheet")
      );
    }
    if (item.label === "Procurement" || item.label === "Vendor-Purchase") {
      return (
        location.pathname.startsWith("/vendor-management") ||
        location.pathname.startsWith("/purchase-management") ||
        location.pathname.startsWith("/purchase-order") ||
        location.pathname.startsWith("/expenses")
      );
    }
    if (item.label === "My Orders" || item.label === "My Sale Orders") {
      return location.pathname.startsWith("/mysaleorder-management");
    }
    if (item.label === "Role Management") {
      return (
        location.pathname.startsWith("/role-management") ||
        location.pathname.startsWith("/access-management") ||
        location.pathname.startsWith("/state-city")
      );
    }
    if (item.path === "/accounts-management") {
      return location.pathname.startsWith("/accounts-management");
    }
    if (item.dropdown) {
      return item.dropdown.some((sub: any) => location.pathname === sub.path);
    }
    return item.path === location.pathname;
  };

  const getVisibleLabel = (item: any) => {
    const label = String(item.label || "");
    // Shorter labels keep the admin bar readable at 100% zoom
    if (label === "Account & Customer") return "Accounts";
    if (label === "Vendor-Purchase") return "Purchase";
    if (label === "Role Management") return "Roles";
    return label;
  };
  const cartAccess = accessList.find((item: any) => item.code === "cart_management");
  const showCart = userType === "CUSTOMER" || !!cartAccess?.read;

  return (
    <>
      <nav className={styles.navWrapper}>
        <div
          className={styles.logo}
          onClick={() => {
            if (userType === "ADMIN" || userType === "EMPLOYEE") navigate("/");
            else navigate("/shop");
          }}
        >
          <img src="/Assets/dm.png" alt="logo" />
        </div>

        <ul className={styles.navLinks}>
          {navConfig.map((item) => {
            if (item.path === "/" && userType === "CUSTOMER") return null;
            if (item.label === "Sales" && userType === "CUSTOMER") return null;
            if (item.label === "My Orders" && (userType === "ADMIN" || userType === "EMPLOYEE"))
              return null;

            if (!canRead(item.code)) return null;

            if (item.dropdown) {
              const isStaff = userType === "ADMIN" || userType === "EMPLOYEE";
              const visibleChildren = item.dropdown.filter((sub: any) => {
                if (sub.staffOnly && !isStaff) return false;
                if (sub.adminOnly && userType !== "ADMIN") return false;
                return canRead(sub.code);
              });
              if (visibleChildren.length === 0) return null;

              return (
                <div
                  key={item.label}
                  className={styles.dropdownWrapper}
                  onMouseEnter={() => openMenu(item.label)}
                  onMouseLeave={scheduleClose}
                >
                  <li
                    className={`${styles.navItem} ${isActive(item) ? styles.active : ""} ${styles.dropdown}`}
                    onMouseEnter={() => openMenu(item.label)}
                    onClick={() =>
                      setOpenDropdown((prev) => (prev === item.label ? null : item.label))
                    }
                  >
                    {getVisibleLabel(item)}
                    <FiChevronDown className={styles.dropdownArrow} />
                  </li>

                  {openDropdown === item.label && (
                    <ul
                      className={styles.dropdownMenu}
                      onMouseEnter={() => openMenu(item.label)}
                      onMouseLeave={scheduleClose}
                    >
                      {visibleChildren.map((sub: any) => (
                        <li
                          key={sub.label}
                          onClick={() => {
                            navigate(sub.path);
                            setOpenDropdown(null);
                          }}
                        >
                          {sub.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            }

            return (
              <li
                key={item.label}
                className={`${styles.navItem} ${isActive(item) ? styles.active : ""}`}
                onClick={() => navigate(item.path)}
              >
                {item.label === "My Orders" ? (
                  <>
                    <FaBoxOpen style={{ marginRight: 7, marginBottom: -2 }} />
                    {getVisibleLabel(item)}
                  </>
                ) : (
                  getVisibleLabel(item)
                )}
              </li>
            );
          })}

          {(userType === "EMPLOYEE" || userType === "ADMIN") && (
            <li
              className={`${styles.navItem} ${
                location.pathname === "/attendance" || location.pathname === "/attendance-admin"
                  ? styles.active
                  : ""
              }`}
              onClick={() => {
                if (userType === "EMPLOYEE") navigate("/attendance");
                else if (userType === "ADMIN") navigate("/attendance-admin");
              }}
            >
              <FaCalendarCheck />
              Attendance
            </li>
          )}
        </ul>

        <div className={styles.rightSection}>
          {user && <NotificationBell />}

          {showCart && (
            <div
              className={styles.cartWrapper}
              onClick={() => navigate("/cart")}
              title="Cart & Checkout"
            >
              <FaShoppingCart size={20} />
              {cartItemCount > 0 && (
                <span className={styles.cartBadge}>
                  {cartItemCount > 99 ? "99+" : cartItemCount}
                </span>
              )}
            </div>
          )}

          <div
            className={styles.profileButton}
            tabIndex={0}
            ref={profileRef}
            onMouseEnter={() => setShowProfileDropdown(true)}
            onMouseLeave={() => setShowProfileDropdown(false)}
            onClick={() => setShowProfileDropdown((prev) => !prev)}
          >
            <img src={"/Assets/no-profile.png"} alt="profile" className={styles.avatar} />
            <span className={styles.userName}>{user?.username || "User"}</span>
            <FiChevronDown className={styles.profileDownArrow} />

            {showProfileDropdown && (
              <div className={styles.profileDropdown}>
                <div className={styles.profileDropdownHeader}>
                  <img
                    src={"/Assets/no-profile.png"}
                    alt="profile"
                    className={styles.dropdownAvatar}
                  />
                  <div>
                    <div className={styles.dropdownUserName}>{user?.username || "User"}</div>
                    {user?.email && <div className={styles.dropdownEmail}>{user.email}</div>}
                    {user?.mobile_number && (
                      <div className={styles.dropdownMobile}>{user.mobile_number}</div>
                    )}
                  </div>
                </div>

                <div
                  className={styles.profileActionRow}
                  onClick={() => setShowChangePasswordModal(true)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setShowChangePasswordModal(true);
                  }}
                >
                  Change Password
                  <span style={{ fontSize: 18, marginLeft: 1 }}>→</span>
                </div>

                {String(userType).toUpperCase() === "ADMIN" && (
                  <div
                    className={styles.profileActionRow}
                    onClick={() => {
                      setShowProfileDropdown(false);
                      setShowSettingsModal(true);
                    }}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setShowProfileDropdown(false);
                        setShowSettingsModal(true);
                      }
                    }}
                  >
                    Settings
                    <span style={{ fontSize: 18, marginLeft: 1 }}>→</span>
                  </div>
                )}

                <div
                  className={styles.profileDropdownFooter}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    padding: "12px 0 0 0",
                  }}
                >
                  <div className={styles.logoutBtnWrapper}>
                    <button className={styles.logoutBtn} onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.versionTag}>
            {VERSION}
          </div>
        </div>
      </nav>

      {showChangePasswordModal && (
        <ChangePasswordModal onClose={() => setShowChangePasswordModal(false)} />
      )}
      {showSettingsModal && (
        <SettingsModal onClose={() => setShowSettingsModal(false)} />
      )}
    </>
  );
};

export default NavbarView;
